import React, { useMemo, useState } from "react";
import { sendContactMessage } from "../../api/contact";
import { useAuth } from "../../contexts/AuthContext";

export default function ContactUsModal({ open, onClose }) {
  const { user } = useAuth() || {};
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [category, setCategory] = useState("");
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const isFormValid = useMemo(() => {
    return Boolean(
      firstName.trim() &&
      category.trim() &&
      message.trim().length >= 10
    );
  }, [firstName, category, message]);

  const reset = () => {
    setFirstName("");
    setLastName("");
    setCategory("");
    setMessage("");
    setErrors({});
  };

  if (!open) return null;

  const validate = () => {
    const next = {};
    if (!firstName.trim()) next.firstName = "First name is required.";
    if (!category.trim()) next.category = "Please choose a category.";
    if (!message.trim() || message.trim().length < 10) {
      next.message = "Tell us a bit more (at least 10 characters).";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validate()) return;
    setLoading(true);
    try {
      await sendContactMessage({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        category: category.trim(),
        message: message.trim(),
        userEmail: user?.email || null,
      });
      window.dispatchEvent(
        new CustomEvent("mudo:toast", {
          detail: {
            type: "success",
            message: "Thanks for reaching out -- your message is on its way to Bark Line Studio.",
          },
        })
      );
      reset();
      onClose?.();
    } catch (err) {
      console.error("[ContactUsModal] send error", err);
      window.dispatchEvent(
        new CustomEvent("mudo:toast", {
          detail: {
            type: "error",
            message: "Something went wrong while sending your message. Please try again.",
          },
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[3500] flex items-center justify-center bg-black/60 px-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0" onClick={() => (!loading ? onClose?.() : null)} />
      <div className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-hidden rounded-3xl border border-white/10 bg-slate-900/80 backdrop-blur-xl shadow-2xl flex flex-col">
        <div className="px-6 md:px-8 pt-6 md:pt-8 pb-4">
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 mb-1">Support</p>
          <h2 className="text-xl md:text-2xl font-semibold text-slate-50">Contact Mudo team</h2>
          <p className="text-sm text-slate-300 mt-1">
            Have a question, found a bug, or dreaming up a new feature? We would love to hear from you.
          </p>
        </div>

        <form
          id="contact-form"
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto px-6 md:px-8 pb-4 space-y-4"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-300 mb-1 block">First name *</label>
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-sm text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-400/50"
                placeholder="Ada"
              />
              {errors.firstName && (
                <p className="text-[11px] text-amber-300 mt-1">{errors.firstName}</p>
              )}
            </div>
            <div>
              <label className="text-xs text-slate-300 mb-1 block">Last name</label>
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-sm text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-400/50"
                placeholder="Lovelace"
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-300 mb-1 block">What is this about? *</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full rounded-xl border border-white/15 bg-slate-900/70 text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400/50"
            >
              <option value="" className="bg-white text-slate-900 hover:bg-slate-800 hover:text-white">
                Select a category
              </option>
              <option className="bg-white text-slate-900 hover:bg-slate-800 hover:text-white">General question</option>
              <option className="bg-white text-slate-900 hover:bg-slate-800 hover:text-white">Bug report</option>
              <option className="bg-white text-slate-900 hover:bg-slate-800 hover:text-white">Feature request</option>
              <option className="bg-white text-slate-900 hover:bg-slate-800 hover:text-white">Billing & subscription</option>
              <option className="bg-white text-slate-900 hover:bg-slate-800 hover:text-white">Workshops & bookings</option>
              <option className="bg-white text-slate-900 hover:bg-slate-800 hover:text-white">Studio setup help</option>
            </select>
            {errors.category && (
              <p className="text-[11px] text-amber-300 mt-1">{errors.category}</p>
            )}
          </div>

          <div>
            <label className="text-xs text-slate-300 mb-1 block">Message *</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-sm text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-400/50 min-h-[120px]"
              placeholder="Tell us what is happening, or what you would love to see in Mudo..."
            />
            {errors.message && (
              <p className="text-[11px] text-amber-300 mt-1">{errors.message}</p>
            )}
            {!errors.message && message.trim().length > 0 && message.trim().length < 10 && (
              <p className="text-[11px] text-slate-400 mt-1">
                {message.trim().length}/10 characters — add a bit more so we can help faster.
              </p>
            )}
          </div>
        </form>

        <div className="px-6 md:px-8 py-4 border-t border-white/10 flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
          <div className="text-xs text-slate-300">We usually reply within 1-2 business days.</div>
          <div className="flex-1" />
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => (!loading ? onClose?.() : null)}
              className="px-4 py-2 rounded-full border border-white/15 bg-white/5 text-sm text-slate-100 hover:bg-white/10 transition"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="contact-form"
              disabled={loading || !isFormValid}
              className={`px-5 py-2 rounded-full text-sm font-semibold text-black transition ${
                !loading && isFormValid
                  ? "bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 shadow-[0_10px_30px_rgba(234,88,12,0.35)]"
                  : "bg-slate-600 text-slate-200 cursor-not-allowed"
              }`}
            >
              {loading ? "Sending..." : "Send message"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
